if (!customElements.get('pet-builder')) {
  customElements.define(
    'pet-builder',
    class PetBuilder extends HTMLElement {
      connectedCallback() {
        this.data = JSON.parse(this.querySelector('[data-builder-json]').textContent);
        this.form = this.querySelector('[data-builder-form]');
        this.panels = [...this.querySelectorAll('[data-step]')];
        this.totalSteps = this.panels.length;
        this.money = new Intl.NumberFormat(this.data.locale === 'en' ? 'en-GB' : this.data.locale, {
          style: 'currency',
          currency: this.data.currency || 'GBP',
        });

        this.el = {
          fill: this.querySelector('[data-progress-fill]'),
          progress: this.querySelector('[data-progress-label]'),
          model: this.querySelector('[data-model]'),
          modelHelp: this.querySelector('[data-model-help]'),
          note: this.querySelector('[data-note]'),
          noteText: this.querySelector('[data-note-text]'),
          colourField: this.querySelector('[data-colour-field]'),
          colourName: this.querySelector('[data-colour-name]'),
          clearColour: this.querySelector('[data-clear-colour]'),
          typeField: this.querySelector('[data-type-field]'),
          year: this.querySelector('[data-year]'),
          photo2Field: this.querySelector('[data-photo2-field]'),
          uploadNow: this.querySelector('[data-upload-now]'),
          uploadLater: this.querySelector('[data-upload-later]'),
          summary: this.querySelector('[data-summary]'),
          total: this.querySelector('[data-total]'),
          back: this.querySelector('[data-back]'),
          next: this.querySelector('[data-next]'),
          nameInput: this.querySelector('[data-count-input]'),
          nameCount: this.querySelector('[data-count]'),
        };

        this.state = { brand: 0 };
        // Phone model → { brand, styles, magsafe }, from assets/phone-compatibility.json
        this.compat = {};

        this.applyUrlPreselect();
        this.bindEvents();
        this.applyStyle(false);
        this.show(1, false);
        this.loadCompatibility();
      }

      // ?style=solid|clear and ?magsafe=yes (or the older ?case=magsafe). Solid is the Tough variant;
      // ?style=tough also works. Invalid values are ignored.
      applyUrlPreselect() {
        const params = new URLSearchParams(window.location.search);
        const aliases = { solid: 'tough' };
        const param = (params.get('style') || '').toLowerCase();
        const style = aliases[param] || param;
        const styleRadio = [...this.querySelectorAll('[data-style]')].find(
          (radio) => radio.value.toLowerCase() === style
        );
        if (styleRadio) styleRadio.checked = true;

        const magsafe = (params.get('magsafe') || '').toLowerCase();
        if (['yes', 'true', '1'].includes(magsafe) || (params.get('case') || '').toLowerCase() === 'magsafe') {
          const radio = this.querySelector('[data-type][value="MagSafe"]');
          if (radio) radio.checked = true;
        }
      }

      async loadCompatibility() {
        try {
          const response = await fetch(this.data.compatibilityUrl);
          if (!response.ok) throw new Error(response.status);
          this.compat = await response.json();
        } catch (error) {
          const message = this.querySelector('[data-error="model"]');
          if (message) message.dataset.message = message.dataset.loadMessage;
          this.setError('model', true);
          return;
        }
        this.models = Object.keys(this.compat);
        this.fillModels();
        this.applyRules(false);
        this.update();
      }

      bindEvents() {
        this.querySelectorAll('[data-brand]').forEach((radio) =>
          radio.addEventListener('change', () => {
            this.state.brand = Number(radio.value);
            this.fillModels();
            this.applyRules(true);
            this.update();
          })
        );

        this.el.model.addEventListener('change', () => {
          if (this.el.model.value) this.setError('model', false);
          this.applyRules(true);
          this.update();
        });

        this.querySelectorAll('[data-style]').forEach((radio) =>
          radio.addEventListener('change', () => {
            this.applyRules(true);
            this.applyStyle();
            this.update();
          })
        );

        // Solid colours and patterns are two radio groups, but only one choice is allowed across both.
        this.querySelectorAll('[data-colour]').forEach((radio) =>
          radio.addEventListener('change', () => {
            this.querySelectorAll('[data-colour]').forEach((other) => {
              if (other.name !== radio.name) other.checked = false;
            });
            if (this.el.colourName) this.el.colourName.textContent = radio.value;
          })
        );

        this.querySelectorAll('[data-addon]').forEach((box) =>
          box.addEventListener('change', () => this.toggleAddon(box))
        );

        if (this.el.year) {
          this.el.year.addEventListener('input', () => {
            this.el.year.value = this.el.year.value.replace(/\D/g, '');
            this.setError('year', false);
          });
        }

        if (this.el.nameInput && this.el.nameCount) {
          this.el.nameInput.addEventListener('input', () => {
            this.el.nameCount.textContent = this.el.nameInput.value.length;
          });
        }

        this.querySelectorAll('[data-photo-when]').forEach((radio) =>
          radio.addEventListener('change', () => this.applyPhotoWhen())
        );

        this.querySelectorAll('[data-upload]').forEach((upload) => this.bindUpload(upload));

        this.querySelectorAll('[data-gallery]').forEach((gallery) => {
          gallery.querySelectorAll('[data-thumb]').forEach((thumb) =>
            thumb.addEventListener('click', () => this.showSlide(gallery, thumb.dataset.thumb))
          );
        });

        this.form.addEventListener('change', () => this.update());
        this.form.addEventListener('submit', (event) => event.preventDefault());
        this.el.next.addEventListener('click', () => this.onNext());
        this.el.back.addEventListener('click', () => this.show(this.step - 1));
      }

      brandOf(model) {
        const brand = this.compat[model] && this.compat[model].brand;
        const index = this.data.brands.findIndex((entry) => entry.match && entry.match === brand);
        return index === -1 ? this.data.brands.length - 1 : index;
      }

      fillModels() {
        const select = this.el.model;
        const current = select.value;
        const placeholder = select.options[0];
        select.replaceChildren(placeholder);
        (this.models || [])
          .filter((model) => this.brandOf(model) === this.state.brand)
          .forEach((model) => select.append(new Option(model, model)));
        select.value = [...select.options].some((option) => option.value === current) ? current : '';
        const brand = this.data.brands[this.state.brand];
        if (this.el.modelHelp && brand) this.el.modelHelp.textContent = brand.help;
      }

      phone() {
        return this.compat[this.el.model.value] || null;
      }

      styleValue() {
        const checked = this.querySelector('[data-style]:checked');
        return checked ? checked.value : 'Tough';
      }

      typeValue() {
        const checked = this.querySelector('[data-type]:checked');
        return checked ? checked.value : 'Standard';
      }

      variantFor(style, type) {
        return this.data.product.variants.find((variant) => variant.style === style && variant.magsafe === type);
      }

      currentVariant() {
        if (!this.el.model.value) return null;
        return this.variantFor(this.styleValue(), this.typeValue()) || null;
      }

      styleAllowed(style, phone = this.phone()) {
        const exists = Boolean(this.variantFor(style, 'Standard') || this.variantFor(style, 'MagSafe'));
        return exists && (!phone || phone.styles.includes(style));
      }

      magsafeAllowed(style, phone = this.phone()) {
        return Boolean(this.variantFor(style, 'MagSafe')) && (!phone || phone.magsafe.includes(style));
      }

      label(value) {
        return this.data.labels[value] || value;
      }

      // Shows only the styles and MagSafe choice the phone supports. If the current choice no
      // longer fits, switches to one that does and, when announce is true, says so.
      applyRules(announce) {
        const phone = this.phone();
        const notes = [];
        const s = this.data.strings;

        const styles = [...this.querySelectorAll('[data-style]')];
        styles.forEach((radio) => {
          const allowed = this.styleAllowed(radio.value, phone);
          radio.closest('.pet-choice').hidden = !allowed;
          radio.disabled = !allowed;
        });

        const style = this.styleValue();
        if (!this.styleAllowed(style, phone)) {
          const fallback = styles.find((radio) => !radio.disabled);
          if (fallback) {
            fallback.checked = true;
            notes.push(s.styleSwitched.replace('[style]', this.label(style)).replace('[fallback]', this.label(fallback.value)));
            this.applyStyle();
          }
        }

        const magsafeOk = this.magsafeAllowed(this.styleValue(), phone);
        if (this.el.typeField) this.el.typeField.hidden = !magsafeOk;
        const magsafe = this.querySelector('[data-type][value="MagSafe"]');
        const standard = this.querySelector('[data-type][value="Standard"]');
        if (!magsafeOk && magsafe && magsafe.checked && standard) {
          standard.checked = true;
          const anyMagsafe = phone && phone.magsafe.length > 0;
          const template = anyMagsafe ? s.magsafeStyleSwitched : s.magsafeSwitched;
          notes.push(
            template
              .replace('[magsafe]', this.label('MagSafe'))
              .replace('[style]', this.label(this.styleValue()))
              .replace('[fallback]', this.label('Standard'))
          );
        }

        if (announce && this.el.note) {
          this.el.noteText.textContent = notes.join(' ');
          this.el.note.hidden = notes.length === 0;
        }
      }

      applyStyle(jumpGallery = true) {
        const isClear = this.styleValue() === 'Clear';
        if (this.el.colourField) {
          this.el.colourField.hidden = isClear;
          this.el.colourField.querySelectorAll('[data-colour]').forEach((radio) => (radio.disabled = isClear));
        }
        if (this.el.clearColour) this.el.clearColour.disabled = !isClear;
        this.applyGalleryStyle(jumpGallery);
      }

      // Hides the other style's images (shared images have no group). With jump, shows the style's
      // variant image; without it, only moves if the current image belongs to another style.
      applyGalleryStyle(jump) {
        const style = this.styleValue();
        const inStyle = (node) => !node.dataset.styleGroup || node.dataset.styleGroup === style;
        this.querySelectorAll('[data-gallery]').forEach((gallery) => {
          const slides = [...gallery.querySelectorAll('[data-slide]')];
          const thumbs = [...gallery.querySelectorAll('[data-thumb]')];
          if (!slides.some((slide) => slide.dataset.styleGroup)) return;

          thumbs.forEach((thumb) => (thumb.hidden = !inStyle(thumb)));
          const row = gallery.querySelector('.pet-gallery__thumbs');
          if (row) row.hidden = thumbs.filter((thumb) => !thumb.hidden).length < 2;

          const current = slides.find((slide) => !slide.hidden);
          if (!jump && current && inStyle(current)) return;
          const target =
            slides.find((slide) => slide.dataset.styleGroup === style && slide.hasAttribute('data-style-start')) ||
            slides.find(inStyle);
          if (target) this.showSlide(gallery, target.dataset.slide);
        });
      }

      toggleAddon(box) {
        const reveal = this.querySelector(`[data-reveal="${box.dataset.addon}"]`);
        if (reveal) {
          reveal.hidden = !box.checked;
          reveal.querySelectorAll('input').forEach((input) => (input.disabled = !box.checked));
        }
        if (box.dataset.addon === 'year' && !box.checked) this.setError('year', false);
        if (box.dataset.addon === 'pet2' && this.el.photo2Field) this.el.photo2Field.hidden = !box.checked;
      }

      photoLater() {
        const checked = this.querySelector('[data-photo-when]:checked');
        return checked && checked.value === 'later';
      }

      applyPhotoWhen() {
        const later = this.photoLater();
        this.el.uploadNow.hidden = later;
        this.el.uploadLater.hidden = !later;
        this.querySelectorAll('[data-photo]').forEach((input) => (input.disabled = later));
        this.setError('photo', false);
        this.renderSummary();
      }

      bindUpload(upload) {
        const input = upload.querySelector('[data-photo]');
        const zone = upload.querySelector('[data-upload-zone]');
        const done = upload.querySelector('[data-upload-done]');
        const thumb = upload.querySelector('[data-upload-thumb]');
        input.addEventListener('change', () => {
          const file = input.files[0];
          if (!file) return;
          if (thumb.src) URL.revokeObjectURL(thumb.src);
          thumb.src = URL.createObjectURL(file);
          upload.querySelector('[data-upload-name]').textContent = file.name;
          zone.hidden = true;
          done.hidden = false;
          if (upload.dataset.upload === '1') this.setError('photo', false);
          this.renderSummary();
        });
        upload.querySelector('[data-upload-change]').addEventListener('click', () => input.click());
      }

      showSlide(gallery, index) {
        gallery.querySelectorAll('[data-slide]').forEach((slide) => (slide.hidden = slide.dataset.slide !== index));
        gallery
          .querySelectorAll('[data-thumb]')
          .forEach((thumb) => thumb.setAttribute('aria-current', thumb.dataset.thumb === index ? 'true' : 'false'));
      }

      selectedAddons() {
        return [...this.querySelectorAll('[data-addon]:checked')]
          .map((box) => this.data.addons[box.dataset.addon])
          .filter(Boolean);
      }

      addonsTotal() {
        return this.selectedAddons().reduce((sum, addon) => sum + addon.price, 0);
      }

      basePrice() {
        const variant = this.variantFor(this.styleValue(), this.typeValue());
        return variant ? variant.price : this.data.product.minPrice;
      }

      format(cents) {
        return this.money.format(cents / 100);
      }

      formatDifference(cents) {
        const whole = cents % 100 === 0;
        const formatter = new Intl.NumberFormat(this.money.resolvedOptions().locale, {
          style: 'currency',
          currency: this.money.resolvedOptions().currency,
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: whole ? 0 : 2,
        });
        return `+${formatter.format(cents / 100)}`;
      }

      update() {
        this.updatePills();
        this.updateAvailability();
        this.el.total.textContent = this.format(this.basePrice() + this.addonsTotal());
        if (this.step === this.totalSteps) this.renderSummary();
      }

      updatePills() {
        const style = this.styleValue();
        const type = this.typeValue();

        const standard = this.variantFor(style, 'Standard');
        const magsafe = this.variantFor(style, 'MagSafe');
        const magsafeCard = this.querySelector('[data-type][value="MagSafe"]');
        if (magsafeCard && standard && magsafe) this.setPill(magsafeCard, magsafe.price - standard.price);

        const clearCard = this.querySelector('[data-style][value="Clear"]');
        const tough = this.variantFor('Tough', type);
        const clear = this.variantFor('Clear', type);
        if (clearCard && tough && clear) this.setPill(clearCard, clear.price - tough.price);
      }

      setPill(input, difference) {
        const pill = input.closest('.pet-choice').querySelector('[data-pill]');
        if (!pill) return;
        pill.hidden = difference <= 0;
        if (difference > 0) pill.querySelector('[data-pill-text]').textContent = this.formatDifference(difference);
      }

      updateAvailability() {
        const style = this.styleValue();
        this.querySelectorAll('[data-type]').forEach((radio) => {
          const variant = this.variantFor(style, radio.value);
          radio.disabled = !(variant && variant.available);
        });
      }

      setError(key, show) {
        const error = this.querySelector(`[data-error="${key}"]`);
        if (!error) return;
        error.hidden = !show;
        error.textContent = show ? error.dataset.message : '';
      }

      validate(step) {
        if (step === 1) {
          const ok = Boolean(this.phone());
          this.setError('model', !ok);
          if (!ok) this.el.model.focus();
          return ok;
        }
        if (step === 2) {
          const variant = this.currentVariant();
          const ok = Boolean(variant && variant.available && this.styleAllowed(variant.style));
          this.setError('variant', !ok);
          return ok;
        }
        if (step === 3) {
          const box = this.querySelector('[data-addon="year"]');
          if (!box || !box.checked) return true;
          const value = this.el.year.value.trim();
          const year = Number(value);
          const ok = /^\d{4}$/.test(value) && year >= 1950 && year <= new Date().getFullYear();
          this.setError('year', !ok);
          if (!ok) this.el.year.focus();
          return ok;
        }
        if (step === 4) {
          const photo = this.querySelector('[data-photo="1"]');
          const ok = this.photoLater() || (photo && photo.files.length > 0);
          this.setError('photo', !ok);
          return ok;
        }
        return true;
      }

      onNext() {
        if (!this.validate(this.step)) return;
        if (this.step < this.totalSteps) {
          this.show(this.step + 1);
        } else {
          this.submit();
        }
      }

      show(step, moveFocus = true) {
        this.step = step;
        this.panels.forEach((panel) => (panel.hidden = Number(panel.dataset.step) !== step));
        this.el.fill.style.width = `${(step / this.totalSteps) * 100}%`;
        this.el.progress.textContent = this.data.strings.stepOf
          .replace('[current]', step)
          .replace('[total]', this.totalSteps);
        this.el.back.hidden = step === 1;
        this.el.next.textContent = this.data.nextLabels[step - 1];
        this.update();
        if (step === this.totalSteps) this.renderSummary();
        if (!moveFocus) return;
        const heading = this.panels[step - 1].querySelector('.pet-step__heading');
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.form.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        if (heading) heading.focus({ preventScroll: true });
      }

      renderSummary() {
        const s = this.data.strings;
        const variant = this.currentVariant();
        const type = this.typeValue();
        const isClear = this.styleValue() === 'Clear';
        const colour = this.querySelector('[data-colour]:checked');
        const caseParts = [isClear ? s.clear : colour ? colour.value : '', type].filter(Boolean);
        const name = this.el.nameInput ? this.el.nameInput.value.trim() : '';
        const photo = this.querySelector('[data-photo="1"]');

        const rows = [
          [s.phone, this.el.model.value || s.notChosen],
          [s.case, caseParts.join(', ')],
          [s.name, name || s.none],
          ...this.selectedAddons().map((addon) => [addon.label, this.formatDifference(addon.price)]),
          [s.photo, this.photoLater() ? s.photoLater : photo && photo.files.length ? s.photoAttached : s.photoMissing],
        ];

        const items = rows.map(([label, value]) => {
          const li = document.createElement('li');
          li.append(Object.assign(document.createElement('span'), { textContent: label }));
          li.append(Object.assign(document.createElement('span'), { textContent: value }));
          return li;
        });
        const total = document.createElement('li');
        total.className = 'pet-summary__total';
        total.append(Object.assign(document.createElement('span'), { textContent: s.total }));
        total.append(
          Object.assign(document.createElement('span'), {
            textContent: this.format((variant ? variant.price : this.basePrice()) + this.addonsTotal()),
          })
        );
        this.el.summary.replaceChildren(...items, total);
      }

      bundleId() {
        if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      }

      buildFormData(variant) {
        const formData = new FormData();
        const bundle = this.bundleId();
        formData.append('items[0][id]', variant.id);
        formData.append('items[0][quantity]', '1');

        // Includes properties[Phone model] from the model select.
        this.form.querySelectorAll('[name^="properties["]').forEach((field) => {
          if (field.disabled) return;
          if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return;
          const key = field.name.slice('properties['.length, -1);
          if (field.type === 'file') {
            if (field.files[0]) formData.append(`items[0][properties][${key}]`, field.files[0]);
            return;
          }
          const value = field.value.trim();
          if (value) formData.append(`items[0][properties][${key}]`, value);
        });

        formData.append('items[0][properties][_upload_later]', this.photoLater() ? 'Yes' : 'No');
        formData.append('items[0][properties][_addons_total]', (this.addonsTotal() / 100).toFixed(2));
        formData.append('items[0][properties][_bundle_id]', bundle);

        this.selectedAddons().forEach((addon, index) => {
          const line = index + 1;
          formData.append(`items[${line}][id]`, addon.variant);
          formData.append(`items[${line}][quantity]`, '1');
          formData.append(`items[${line}][parent_id]`, variant.id);
          formData.append(`items[${line}][properties][_bundle_id]`, bundle);
        });

        return formData;
      }

      async submit() {
        const variant = this.currentVariant();
        if (!this.phone()) {
          this.show(1);
          this.setError('model', true);
          return;
        }
        if (!variant || !variant.available) {
          this.show(2);
          this.setError('variant', true);
          return;
        }

        const cart = document.querySelector('cart-drawer');
        const formData = this.buildFormData(variant);
        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
        }

        const label = this.el.next.textContent;
        this.el.next.disabled = true;
        this.el.next.setAttribute('aria-busy', 'true');
        this.el.next.textContent = this.data.strings.adding;
        this.setError('submit', false);

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          const state = await response.json();
          if (!response.ok || state.status) {
            const error = this.querySelector('[data-error="submit"]');
            if (state.description) error.dataset.message = state.description;
            this.setError('submit', true);
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pet-builder', productVariantId: variant.id, cartData: state });
          }

          if (cart && typeof cart.renderContents === 'function') {
            cart.classList.remove('is-empty');
            cart.renderContents(state);
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (error) {
          this.setError('submit', true);
        } finally {
          this.el.next.disabled = false;
          this.el.next.removeAttribute('aria-busy');
          this.el.next.textContent = label;
        }
      }
    }
  );
}
